import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";

import { RenewalPage } from "@/config/app-constant";
import fetchGenericError from "@/hooks/magnolia/fetch-generic-errors";
import fetchSessionExpireError from "@/hooks/magnolia/fetch-session-expire";
import {
  clearApiError,
  setApiError,
} from "@/store/slices/errors/api-error.action";
import { apiErrorMappings } from "@/utils/errors/api-error-map";

export interface ErrorResponse {
  code?: number;
  errors?: {
    code: number;
    message: string;
  }[];
}

const handleError = (
  error: AxiosError<ErrorResponse>,
  router: ReturnType<typeof useRouter>,
  dispatch: ReturnType<typeof useDispatch>
) => {
  const errorResponse = error.response;
  const errorCode = errorResponse?.data?.errors?.[0]?.code;
  const applicationName = process.env.NEXT_PUBLIC_APPLICATION ?? "quote";

  const handleGenericError = (errorStatusType: string) => {
    const errorObjectValue = apiErrorMappings(error);
    errorObjectValue.multiButton = false;
    fetchGenericError(errorStatusType).then((data) => {
      const errorObject = apiErrorMappings(error);
      errorObject.ctaPrimaryBtnLabel = data?.ctaPrimaryBtnLabel ?? "Go back";
      errorObject.modalTitle = data?.title ?? "Something <br> went wrong";
      errorObject.modalDescription =
        data?.modalDescription ??
        "Please try again. If the problem persists, please contact us.";
      errorObject.onPrimaryCtaClick =
        errorStatusType === "4XX"
          ? ({ handleModalClose }) => {
              router.back();
              dispatch(clearApiError());
              handleModalClose(false);
            }
          : () => router.push(process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!);
      dispatch(setApiError(errorObject));
    });
  };

  const is4XX =
    errorResponse?.status &&
    /^4\d{2}$/.test(errorResponse?.status as unknown as string);
  const is5XX =
    errorResponse?.status &&
    /^5\d{2}$/.test(errorResponse?.status as unknown as string);

  if (
    errorResponse?.status === 401 &&
    applicationName === RenewalPage &&
    errorCode === 4001
  ) {
    fetchSessionExpireError().then((data) => {
      const errorObject = apiErrorMappings(error);
      errorObject.errorStatus = true;
      errorObject.validationEnable = false;
      errorObject.multiButton = false;
      errorObject.ctaPrimaryBtnLabel =
        data?.ctaButtonText ?? "Back to My Staysure";
      errorObject.modalTitle =
        data?.title ?? "Your session has <br/> timed out";
      errorObject.modalDescription = "";
      errorObject.isCloseIconEnabled = false;
      errorObject.onPrimaryCtaClick = router
        ? () => router.push(process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!)
        : // eslint-disable-next-line func-names
          function () {
            window.location.href = process.env.NEXT_PUBLIC_MY_ACCOUNT_URL!;
          };

      dispatch(setApiError(errorObject));
    });
  } else if ((is4XX || is5XX) && applicationName === RenewalPage) {
    handleGenericError(is5XX ? "5XX" : "4XX");
  }
};

export default handleError;
